import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { isAccessible, isTrackerOnline } from '@racemap/utilities/functions/trackers';
import { Flex, Tag } from 'antd';
import type { Immutable } from 'immer';
import type { TableCellProps } from 'react-virtualized';
import { useCurrentUser } from '../../../../lib/customHooks';
import type { ManagedTracker } from '../../../../store/trackers/trackers_reducers';
import { RelativeTime } from '../../../BasicComponents/TimeDuration/RelativeTime';
import { Columns } from '../../../DataTable';
import {
  LocationFilters,
  getLocationRank,
  getLocationState,
} from '../../Filter/FilterTreeFunctions';

interface CellProps extends TableCellProps {
  cellData?: Immutable<ManagedTracker['state']>;
  rowData: Immutable<ManagedTracker>;
}

export const LocationColumn = () => {
  const currentUser = useCurrentUser();

  const cellRenderer = ({ rowData }: CellProps) => {
    if (rowData == null || !isAccessible(rowData, currentUser)) return <></>;
    const locationState = getLocationState(rowData);
    const isConnected = isTrackerOnline(rowData.state);

    return (
      <Container justify="flex-end" gap="8px">
        {locationState !== LocationFilters.NoLocation &&
          rowData.lastGeo.lastTime != null &&
          // check that last time is not the default value
          rowData.lastGeo.lastTime.getTime() !== new Date(0).getTime() && (
            <Timestamp>
              <RelativeTime time={rowData.lastGeo.lastTime} size={'short'} />
            </Timestamp>
          )}
        {getBadge(locationState, isConnected)}
      </Container>
    );
  };

  return Columns.sortingColumn<ManagedTracker, boolean>({
    dataKey: 'state.gpsFix',
    label: 'Location',
    headerTitle: 'Time elapsed since the device last recorded its location.',
    cellRenderer,
    width: 135,
    minWidth: 135,
    style: { justifyContent: 'center' },
    sortingMethod: (_cA, _cB, rowDataA, rowDataB) => {
      const aV = getLocationRank(getLocationState(rowDataA));
      const bV = getLocationRank(getLocationState(rowDataB));
      const aTime =
        rowDataA.lastGeo.lastTime != null ? new Date(rowDataA.lastGeo.lastTime).valueOf() : -1;
      const bTime =
        rowDataB.lastGeo.lastTime != null ? new Date(rowDataB.lastGeo.lastTime).valueOf() : -1;

      if (aV !== bV) return bV - aV;
      return bTime - aTime;
    },
  });
};

const Timestamp = styled.div`
  font-size: smaller;
  color: ${RacemapColors.DarkGray};
`;

const getBadge = (state: LocationFilters, isConnected: boolean | undefined | null) => {
  let color = 'danger';
  let text = 'No Location';
  let title = '';

  if (!isConnected) {
    color = 'default';
    text = '---';
  } else {
    switch (state) {
      case LocationFilters.Fix:
        color = 'success';
        text = 'Fix';
        break;
      case LocationFilters.NoFix:
        color = 'warning';
        text = 'NoF';
        break;
      case LocationFilters.Cell:
        color = 'warning';
        text = 'Cell';
        break;
      default:
        color = 'error';
        text = 'NoL';
        title = 'No Location';
    }
  }

  return (
    <Badge color={color} title={title}>
      {text}
    </Badge>
  );
};

const Badge = styled(Tag)<{ color: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 36px;
  line-height: 18px;
  ${({ color }) => color === 'secondary' && 'background-color: #999999;'}
`;

const Container = styled(Flex)`
  width: 100%;
`;
